'use client';

import React, { useMemo } from 'react';
import { CriteriaTemplate, CriteriaInput } from '../../types/criteriaTemplate';

interface TemplateCardProps {
  template: CriteriaTemplate;
  isSelected: boolean;
  onClick: () => void;
}

const PLACEHOLDER_REGEX = /\{\{\s*(\w+)\s*\}\}/g;

const transformToTypeDisplay = (
  template: string,
  inputs: Record<string, CriteriaInput>
): string => {
  return template.replace(PLACEHOLDER_REGEX, (match, inputName) => {
    const componentType = inputs[inputName]?.type;
    if (!componentType) {
      return match;
    }
    return `{{ ${componentType} }}`;
  });
};

const isPatientJourneyTemplate = (template: CriteriaTemplate): boolean => {
  const config = template.config;
  return !!(config.index_event_template || config.target_event_template);
};

/**
 * Get display text for regular template preview
 */
const getRegularTemplatePreview = (template: CriteriaTemplate): string => {
  const config = template.config;
  if (config.criteria_template && config.criteria_inputs) {
    return transformToTypeDisplay(
      config.criteria_template,
      config.criteria_inputs
    );
  }
  return '';
};

/**
 * Get labeled preview parts for patient journey templates
 */
const getPatientJourneyPreviews = (
  template: CriteriaTemplate
): { indexEvent: string | null; targetEvent: string | null } => {
  const config = template.config;

  const indexEvent = config.index_event_template?.criteria_template
    ? transformToTypeDisplay(
        config.index_event_template.criteria_template,
        config.index_event_template.criteria_inputs
      )
    : null;

  const targetEvent = config.target_event_template?.criteria_template
    ? transformToTypeDisplay(
        config.target_event_template.criteria_template,
        config.target_event_template.criteria_inputs
      )
    : null;

  return { indexEvent, targetEvent };
};

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  isSelected,
  onClick,
}) => {
  const isPatientJourney = useMemo(
    () => isPatientJourneyTemplate(template),
    [template]
  );

  const regularPreview = useMemo(
    () => (isPatientJourney ? '' : getRegularTemplatePreview(template)),
    [template, isPatientJourney]
  );

  const journeyPreviews = useMemo(
    () => (isPatientJourney ? getPatientJourneyPreviews(template) : null),
    [template, isPatientJourney]
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-all ${
        isSelected
          ? 'border-sky-600 bg-sky-50'
          : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
      }`}
      data-testid={`template-card-${template.id}`}
      aria-pressed={isSelected}
    >
      <div className="flex flex-col gap-1">
        <h4 className="font-semibold text-primary text-sm leading-tight">
          {template.name}
        </h4>
        <p className="text-xs text-tertiary leading-tight">
          {template.description}
        </p>
        {isPatientJourney && journeyPreviews ? (
          <div className="flex flex-col gap-2 mt-2">
            {journeyPreviews.indexEvent && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-neutral-500">
                  Index Event
                </span>
                <span className="text-xs text-neutral-400 font-mono leading-tight">
                  {journeyPreviews.indexEvent}
                </span>
              </div>
            )}
            {journeyPreviews.targetEvent && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-neutral-500">
                  Target Event
                </span>
                <span className="text-xs text-neutral-400 font-mono leading-tight">
                  {journeyPreviews.targetEvent}
                </span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-neutral-400 mt-2 font-mono leading-tight">
            {regularPreview}
          </p>
        )}
      </div>
    </button>
  );
};

export default TemplateCard;
